/**
 * WAF Multi-CDN analytics (Owl) — how a subdomain's traffic is served across CDN
 * platforms. Four data sources, one per chip on the Multi-CDN page, each its own
 * Owl method / endpoint (note: NO `interval` param — the vendor fixes bucketing):
 *   - total      → GetMultiCDN            → `/services/:service_id/waf/analytics/multicdn/edge`
 *   - origin     → GetMultiCDNOrigin      → `/services/:service_id/waf/analytics/multicdn/origin`
 *   - cacheRate  → GetMultiCDNCacheRate   → `/services/:service_id/waf/analytics/multicdn/cacherate`
 *   - geo        → GetMultiCDNGeolocation → `/services/:service_id/waf/analytics/multicdn/geolocation`
 * Service-scoped (path `/services/:service_id/waf/…`) + `?domain=`.
 *
 * The three backend report shapes differ (edge = a bare dataset array with NO
 * labels; origin/geo = a traffic report with labels + summary; cacherate = a
 * cache-hit-rate report with nullable `%` series). We **normalize** all of them
 * in the query fn to one shape — `{ labels, datasets:{label,traffic,request},
 * summary, isRate }` — so the page + transforms stay uniform. Each dataset
 * carries both a `traffic` and a `request` series, so the page's Traffic/Request
 * tabs select which to render without a new call.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/** One of the four Multi-CDN data sources (chip → endpoint). */
export type WafMultiCdnKind = 'total' | 'origin' | 'cacheRate' | 'geo';
/** Which series the Traffic / Request tabs select within a dataset. */
export type WafMultiCdnMetric = 'traffic' | 'request';

/** Normalized per-entity series. `label` = CDN platform id, or country (geo). */
export interface WafMultiCdnDataset {
  label: string;
  traffic: (number | null)[];
  request: (number | null)[];
}

/** Normalized per-entity range total (bytes/requests, or `%` hit-rate for cacheRate). */
export interface WafMultiCdnSummary {
  label: string;
  traffic: number | null;
  request: number | null;
}

/**
 * Normalized Multi-CDN diagram: shared x-axis `labels` (unix ms) + per-entity
 * `datasets` + range `summary`. `isRate` → values are cache-hit-rate percent
 * (0..100), so the page formats `%` and hides the total/divide controls.
 */
export interface WafMultiCdnDiagram {
  labels: number[];
  datasets: WafMultiCdnDataset[];
  summary: WafMultiCdnSummary[];
  isRate: boolean;
}

export interface WafMultiCdnFilter {
  /** Domain name (the subdomain FQDN). */
  domain: string;
  /** Start unix seconds. */
  start_time: number;
  /** End unix seconds. */
  end_time: number;
}

/** Endpoint path segment for each kind (vendor-cased, no hyphens). */
const KIND_PATH: Record<WafMultiCdnKind, string> = {
  total: 'edge',
  origin: 'origin',
  cacheRate: 'cacherate',
  geo: 'geolocation',
};

// ---- Raw backend shapes (providers.MultiCDN* in provider_waf.go) ----------

interface RawTrafficDataset {
  label: string;
  total_traffic?: number[];
  total_request?: number[];
}
interface RawTrafficSummary {
  label: string;
  total_traffic?: number;
  total_request?: number;
}
interface RawTrafficReport {
  labels?: number[];
  datasets?: RawTrafficDataset[];
  summary?: RawTrafficSummary[];
}
interface RawCacheDataset {
  label: string;
  cache_hitrate_traffic?: (number | null)[];
  cache_hitrate_request?: (number | null)[];
}
interface RawCacheSummary {
  label: string;
  cache_hitrate_traffic?: number | null;
  cache_hitrate_request?: number | null;
}
interface RawCacheReport {
  labels?: number[];
  datasets?: RawCacheDataset[];
  summary?: RawCacheSummary[];
}

const sum = (xs: number[] | undefined) => (xs ?? []).reduce((a, b) => a + (Number(b) || 0), 0);

/**
 * The edge report has no `labels` — fabricate evenly-spaced unix-ms timestamps
 * across [start, end] for `n` buckets so the chart has an x-axis.
 */
function fabricateLabels(startSec: number, endSec: number, n: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [startSec * 1000];
  const step = (endSec - startSec) / n;
  return Array.from({ length: n }, (_, i) => Math.round((startSec + i * step) * 1000));
}

/** Map any of the three backend report shapes to the normalized diagram. */
function normalize(kind: WafMultiCdnKind, data: unknown, startSec: number, endSec: number): WafMultiCdnDiagram {
  if (kind === 'cacheRate') {
    const r = (data ?? {}) as RawCacheReport;
    return {
      labels: r.labels ?? [],
      datasets: (r.datasets ?? []).map((d) => ({
        label: d.label,
        traffic: d.cache_hitrate_traffic ?? [],
        request: d.cache_hitrate_request ?? [],
      })),
      summary: (r.summary ?? []).map((s) => ({
        label: s.label,
        traffic: s.cache_hitrate_traffic ?? null,
        request: s.cache_hitrate_request ?? null,
      })),
      isRate: true,
    };
  }
  if (kind === 'total') {
    // Edge: a bare dataset array, no labels, no summary → fabricate both.
    const arr = (Array.isArray(data) ? data : []) as RawTrafficDataset[];
    const n = arr.reduce((m, d) => Math.max(m, d.total_traffic?.length ?? 0, d.total_request?.length ?? 0), 0);
    return {
      labels: fabricateLabels(startSec, endSec, n),
      datasets: arr.map((d) => ({ label: d.label, traffic: d.total_traffic ?? [], request: d.total_request ?? [] })),
      summary: arr.map((d) => ({ label: d.label, traffic: sum(d.total_traffic), request: sum(d.total_request) })),
      isRate: false,
    };
  }
  // origin | geo: traffic report with labels + summary.
  const r = (data ?? {}) as RawTrafficReport;
  return {
    labels: r.labels ?? [],
    datasets: (r.datasets ?? []).map((d) => ({
      label: d.label,
      traffic: d.total_traffic ?? [],
      request: d.total_request ?? [],
    })),
    summary: (r.summary ?? []).map((s) => ({
      label: s.label,
      traffic: s.total_traffic ?? 0,
      request: s.total_request ?? 0,
    })),
    isRate: false,
  };
}

/**
 * CDN platform IDs that do NOT support a given Multi-CDN report (the dashboard's
 * "metric unavailable on these CDNs" note). Service-scoped, NOT per-domain; only
 * meaningful for `origin` / `cacheRate` / `geo` (the `total`/edge report has no
 * support gaps). `kind` = the same vendor segment as the diagram path.
 */
export function useWafMultiCdnUnsupported(
  serviceId: string | undefined,
  kind: WafMultiCdnKind,
  enabled = true,
) {
  const vendorKind = KIND_PATH[kind];
  const params = { kind: vendorKind };
  return useQuery<string[]>({
    queryKey: wafKeys.multiCdnUnsupported(serviceId ?? '', vendorKind),
    enabled: !!serviceId && kind !== 'total' && enabled,
    staleTime: 300_000, // platform support rarely changes
    queryFn: async () =>
      unwrap<string[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/analytics/multicdn/unsupported`, { params }),
      ) ?? [],
  });
}

/** Multi-CDN diagram for one domain, for the given data source (normalized). */
export function useWafMultiCdnDiagram(
  serviceId: string | undefined,
  kind: WafMultiCdnKind,
  filter: WafMultiCdnFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<WafMultiCdnDiagram>({
    queryKey: wafKeys.multiCdnDiagram(serviceId ?? '', kind, params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.domain && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const data = unwrap<unknown>(
        await serviceApiV3.get(`/services/${serviceId}/waf/analytics/multicdn/${KIND_PATH[kind]}`, { params }),
      );
      return normalize(kind, data, filter.start_time, filter.end_time);
    },
  });
}
